import React, { useState, useRef, useEffect } from "react";
import { Paper } from "@mui/material";
import { arrayMove } from "@dnd-kit/sortable";
import { useFetchSectionsByStatus } from "../../hooks/useSections/useFetchSectionsByStatus";
import { useUpdateSectionOrder } from "../../hooks/useSections/useUpdateSectionOrder";
import { AddSection } from "./panelSections/AddSection";
import { EditSection } from "./panelSections/EditSection";
import { DeleteSection } from "./panelSections/DeleteSection";
import { RestoreSection } from "./panelSections/RestoreSection";
import { SectionToolbar } from "./panelSections/SectionToolbar";
import { SectionList } from "./panelSections/SectionList";

export const SectionsPanel = ({ token, idDocument, nameDocument }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const { sections, loading, refresh } =
    useFetchSectionsByStatus(token, idDocument);
  const { updateSectionOrder } = useUpdateSectionOrder(token);

  const [orderedSections, setOrderedSections] = useState([]);

  const [modals, setModals] = useState({
    add: false,
    edit: false,
    delete: false,
    restore: false,
  });
  const [selectedSection, setSelectedSection] = useState(null);
  const scrollableRef = useRef(null);

  const [activeFilter, setActiveFilter] = useState(undefined);

  useEffect(() => {
    if (sections.length > 0) {
      const sorted = [...sections].sort(
        (a, b) => (a.ORDEN ?? 0) - (b.ORDEN ?? 0)
      );
      setOrderedSections(sorted);
    }
  }, [sections]);

  const handleFilter = (status) => {
    setActiveFilter(status);
  };

  const activeSections = sections.filter(
    (section) => section.FLG_ACTIVO === 1 || section.FLG_ACTIVO === "1"
  ).length;

  const inactiveSections = sections.filter(
    (section) => section.FLG_ACTIVO === 0 || section.FLG_ACTIVO === "0"
  ).length;

  const handleModal = (modalType, isOpen, section = null) => {
    setModals((prev) => ({ ...prev, [modalType]: isOpen }));
    if (section !== undefined) setSelectedSection(section);
  };

  const handleRefresh = () => {
    setActiveFilter(undefined);
    refresh();
  };

  const displaySections = orderedSections.filter((item) => {
    if (activeFilter === undefined) return true;
    return String(item.FLG_ACTIVO) === String(activeFilter);
  });

  const processedData = displaySections.filter((item) =>
    item.NOMBRE_SECCION.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedSections((current) => {
      const oldIndex = current.findIndex(
        (item) => item.ID_SECCION === active.id
      );
      const newIndex = current.findIndex(
        (item) => item.ID_SECCION === over.id
      );

      if (oldIndex === -1 || newIndex === -1) return current;

      const reordered = arrayMove(current, oldIndex, newIndex).map(
        (item, index) => ({ ...item, ORDEN: index })
      );

      const movedItem = reordered[newIndex];
      updateSectionOrder(movedItem.ID_SECCION, movedItem.ORDEN);

      return reordered;
    });
  };

  return (
    <Paper
      elevation={2}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <SectionToolbar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleModal={handleModal}
        handleFilter={handleFilter}
        sectionsCount={sections.length}
        activeSectionsCount={activeSections}
        inactiveSectionsCount={inactiveSections}
      />

      <SectionList
        loading={loading}
        filterLoading={false}
        processedData={processedData}
        searchTerm={searchTerm}
        handleModal={handleModal}
        scrollableRef={scrollableRef}
        onDragEnd={handleDragEnd}
      />

      <AddSection
        token={token}
        open={modals.add}
        close={() => handleModal("add", false)}
        nameDocument={nameDocument}
        id_document={idDocument}
        refresh={handleRefresh}
      />

      <EditSection
        token={token}
        open={modals.edit}
        close={() => handleModal("edit", false, null)}
        section={selectedSection}
        refresh={handleRefresh}
      />

      <DeleteSection
        token={token}
        open={modals.delete}
        close={() => handleModal("delete", false, null)}
        section={selectedSection}
        refresh={handleRefresh}
      />

      <RestoreSection
        token={token}
        open={modals.restore}
        close={() => handleModal("restore", false, null)}
        section={selectedSection}
        refresh={handleRefresh}
      />
    </Paper>
  );
};
